import { useEffect, useRef, useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const clients = [
  {
    name: 'Volvo',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Volvo_wordmark.svg',
    description: 'Concesionaria Temuco',
  },
  {
    name: 'Nestlé',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e4/Nestl%C3%A9.svg',
    description: 'Galpones Temuco',
  },
  {
    name: 'Semillas Generación 2000',
    logo: null,
    icon: 'ri-seedling-line',
    description: 'Planta Chillán',
  },
  {
    name: 'FEPASA',
    logo: null,
    icon: 'ri-train-line',
    description: 'Infraestructura',
  },
  {
    name: 'Minera El Toqui',
    logo: null,
    icon: 'ri-hammer-line',
    description: 'Instalaciones industriales',
  },
  {
    name: 'Mercato',
    logo: null,
    icon: 'ri-store-2-line',
    description: 'Local comercial Temuco',
  },
  {
    name: 'La Estrella',
    logo: null,
    icon: 'ri-shopping-cart-2-line',
    description: 'Supermercado Cunco',
  },
  {
    name: 'BlackHouse Gym',
    logo: null,
    icon: 'ri-boxing-line',
    description: 'Gimnasio Temuco',
  },
];

const stats = [
  { value: '+25', label: 'Años de experiencia', icon: 'ri-award-line' },
  { value: '+200', label: 'Proyectos ejecutados', icon: 'ri-building-2-line' },
  { value: '+50', label: 'Clientes satisfechos', icon: 'ri-group-line' },
  { value: '100%', label: 'Compromiso con la calidad', icon: 'ri-shield-check-line' },
];

const ClientsSection = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const posRef = useRef(0);
  const rafRef = useRef<number>(0);

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation();

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const speed = 0.5;

    const animate = () => {
      if (!paused) {
        posRef.current -= speed;
        const totalWidth = track.scrollWidth / 2;
        if (Math.abs(posRef.current) >= totalWidth) {
          posRef.current = 0;
        }
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [paused]);

  const doubled = [...clients, ...clients];

  return (
    <section className="py-16 bg-[#F8FAFC] overflow-hidden">
      <div
        ref={headerRef}
        className={`max-w-7xl mx-auto px-4 md:px-8 mb-10 text-center ${headerVisible ? 'reveal-visible' : ''} reveal`}
      >
        <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Confianza</span>
        <h2 className="text-3xl md:text-4xl font-black text-[#0C1D2E] mt-2 mb-3">
          Clientes que nos eligen
        </h2>
        <p className="text-[#475569] text-base max-w-xl mx-auto">
          Empresas líderes en sus rubros confían en IngemetalSur para sus proyectos de construcción en todo Chile.
        </p>
      </div>

      {/* Scrolling strip */}
      <div
        className="relative w-full overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Fade edges */}
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-[#F8FAFC] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-[#F8FAFC] to-transparent z-10 pointer-events-none" />

        <div ref={trackRef} className="flex gap-6 w-max">
          {doubled.map((client, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-52 bg-white rounded-lg border border-gray-100 px-6 py-5 flex flex-col items-center justify-center gap-3 transition-all duration-300 hover:border-[#1A3A5C]/30 hover:-translate-y-1 cursor-default"
            >
              {client.logo ? (
                <div className="w-40 h-16 flex items-center justify-center">
                  <img
                    src={client.logo}
                    alt={`Logo ${client.name}`}
                    className="max-w-full max-h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              ) : (
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#F0F0F0]">
                  <i className={`${client.icon} text-[#C88A2A] text-2xl`}></i>
                </div>
              )}
              <div className="text-center">
                <p className="text-[#0C1D2E] font-bold text-sm">{client.name}</p>
                <p className="text-[#64748B] text-xs mt-0.5">{client.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div
        ref={statsRef}
        className="max-w-7xl mx-auto px-4 md:px-8 mt-12"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`bg-white rounded-lg border border-gray-100 p-5 flex items-center gap-4 reveal ${statsVisible ? 'reveal-visible' : ''} reveal-delay-${(i + 1) * 100}`}
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F0F0F0] flex-shrink-0">
                <i className={`${stat.icon} text-[#C88A2A] text-lg`}></i>
              </div>
              <div>
                <p className="text-2xl font-black text-[#0C1D2E] leading-none">{stat.value}</p>
                <p className="text-[#475569] text-xs mt-1">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ClientsSection;